/**
 * UTM Tracking Utility for Vendor Links
 * Handles building URLs with UTM parameters for vendor referral tracking
 */

import { type User } from '@/services/api';

/**
 * Context constants for consistent usage
 */
export const UTM_CONTEXTS = {
    PRODUCT_PAGE: 'product_page',
    COMPARISON_GRID: 'comparison_grid',
    SEARCH_RESULTS: 'search_results',
    IMAGE_CLICK: 'image_click'
} as const;

export type UTMContext = typeof UTM_CONTEXTS[keyof typeof UTM_CONTEXTS];

/**
 * Minimal interface for listings used in UTM tracking
 */
interface UTMTrackableListing {
    _id: string;
    vendor: string;
    listing_url: string;
    affiliate_listing_url?: string | null;
}

/**
 * Builds a vendor URL with UTM tracking parameters
 * @param {UTMTrackableListing} listing - The listing object containing vendor URLs
 * @param {UTMContext} context - The context where the click originated (product_page, comparison_grid, search_results, image_click)
 * @param {Record<string, string>} options - Additional options for UTM parameters
 * @returns {string} - The vendor URL with UTM parameters
 */
export const buildVendorURL = (
    listing: UTMTrackableListing,
    context: UTMContext = UTM_CONTEXTS.PRODUCT_PAGE,
    options: Record<string, string> = {}
): string => {
    if (!listing) return '';

    // If affiliate link exists, use it as-is (don't add UTM tracking)
    if (listing.affiliate_listing_url) {
        return listing.affiliate_listing_url;
    }

    // For non-affiliate vendors, add UTM tracking to the regular URL
    const baseUrl = listing.listing_url;
    if (!baseUrl) return '';

    const utmParams = new URLSearchParams({
        utm_source: 'secondsense',
        utm_medium: 'referral',
        utm_campaign: 'product_discovery',
        utm_content: context,
        ...options // Allow override of any UTM parameters
    });

    // Check if URL already has query parameters
    const separator = baseUrl.includes('?') ? '&' : '?';
    const finalUrl = `${baseUrl}${separator}${utmParams.toString()}`;

    return finalUrl;
};

/**
 * Enhanced event logging with UTM tracking metadata
 * @param {Function} logEvent - The existing logEvent function
 * @param {User | null} user - User object
 * @param {string} eventType - Event type constant
 * @param {UTMTrackableListing} listing - Listing object
 * @param {UTMContext} context - Click context
 * @param {Record<string, unknown>} additionalMetadata - Any additional metadata
 */
export const logVendorClickEvent = (
    logEvent: (user: User | null, eventType: string, metadata: Record<string, unknown>) => void,
    user: User | null,
    eventType: string,
    listing: UTMTrackableListing,
    context: UTMContext,
    additionalMetadata: Record<string, unknown> = {}
): void => {
    const utmUrl = buildVendorURL(listing, context);

    logEvent(user, eventType, {
        listing_id: listing._id,
        vendor: listing.vendor,
        click_context: context,
        utm_url: utmUrl,
        has_affiliate_link: !!listing.affiliate_listing_url,
        ...additionalMetadata
    });
}; 